import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { User, Calendar, Building, Tag } from 'lucide-react';

const COLUMN_COLORS = {
  // Idea statuses
  'Draft': { bg: 'bg-slate-50', border: 'border-slate-200', header: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  'Submitted': { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'Under Review': { bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  'Approved': { bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Rejected': { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  'Converted': { bg: 'bg-purple-50', border: 'border-purple-200', header: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  // Project statuses
  'Not Started': { bg: 'bg-slate-50', border: 'border-slate-200', header: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  'In Progress': { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'Blocked': { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Cancelled': { bg: 'bg-slate-50', border: 'border-slate-200', header: 'bg-slate-100 text-slate-500', dot: 'bg-slate-400' },
  // Types
  'Quick Win': { bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Tech Project': { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'DMAIC': { bg: 'bg-purple-50', border: 'border-purple-200', header: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
};

const DEFAULT_COL = { bg: 'bg-slate-50', border: 'border-slate-200', header: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' };

const KanbanBoard = ({
  items,
  groupByField,
  groupByOptions,     // [{value, label}] for the group-by selector
  columnOrder,        // ordered list of column values for current groupBy
  onGroupByChange,
  onItemDrop,         // (itemId, newColumnValue) => void — called on drag-drop
  cardConfig,         // { title, subtitle, badges, meta, link } — field mappings
  entityType,         // 'idea' or 'project'
}) => {
  const navigate = useNavigate();
  const [dragItem, setDragItem] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragCounterRef = useRef({});

  // Group items into columns
  const columns = {};
  columnOrder.forEach(col => { columns[col] = []; });
  items.forEach(item => {
    const val = item[groupByField] || 'Unknown';
    if (!columns[val]) columns[val] = [];
    columns[val].push(item);
  });

  const handleDragStart = (e, item) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  };

  const handleDragOver = (e, col) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(col);
  };

  const handleDragLeave = (col) => {
    setDragOverCol(prev => prev === col ? null : prev);
  };

  const handleDrop = (e, col) => {
    e.preventDefault();
    setDragOverCol(null);
    if (dragItem && dragItem[groupByField] !== col && onItemDrop) {
      onItemDrop(dragItem.id, col);
    }
    setDragItem(null);
  };

  return (
    <div data-testid="kanban-board">
      {/* Group-by selector */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-slate-500">Group by:</span>
        <Select value={groupByField} onValueChange={onGroupByChange}>
          <SelectTrigger className="w-40 h-8 text-sm" data-testid="kanban-group-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {groupByOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
        {columnOrder.map(col => {
          const colItems = columns[col] || [];
          const colors = COLUMN_COLORS[col] || DEFAULT_COL;
          const isOver = dragOverCol === col;

          return (
            <div
              key={col}
              className={`flex-shrink-0 w-72 rounded-xl border-2 ${isOver ? 'border-[#235894] bg-[#235894]/5' : colors.border + ' ' + colors.bg} transition-colors`}
              onDragOver={(e) => handleDragOver(e, col)}
              onDragLeave={() => handleDragLeave(col)}
              onDrop={(e) => handleDrop(e, col)}
              data-testid={`kanban-col-${col.replace(/\s+/g, '-')}`}
            >
              {/* Column header */}
              <div className={`px-3 py-2.5 rounded-t-lg ${colors.header} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                  <span className="font-semibold text-sm">{col}</span>
                </div>
                <span className="text-xs font-medium opacity-70 bg-white/50 rounded-full px-2 py-0.5">{colItems.length}</span>
              </div>

              {/* Cards */}
              <div className="p-2 space-y-2 min-h-[100px]">
                {colItems.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-6">No items</p>
                )}
                {colItems.map(item => {
                  const cfg = cardConfig;
                  return (
                    <Card
                      key={item.id}
                      className={`p-3 cursor-pointer hover:shadow-md transition-shadow bg-white ${dragItem?.id === item.id ? 'opacity-50' : ''}`}
                      draggable={!!onItemDrop}
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={() => { setDragItem(null); setDragOverCol(null); }}
                      onClick={() => navigate(cfg.link(item))}
                      data-testid={`kanban-card-${item.id}`}
                    >
                      <p className="font-medium text-sm text-slate-900 line-clamp-2 mb-2">
                        {typeof cfg.title === 'function' ? cfg.title(item) : item[cfg.title]}
                      </p>

                      {cfg.badges && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {cfg.badges(item).filter(Boolean).map((b, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] py-0 px-1.5">{b}</Badge>
                          ))}
                        </div>
                      )}

                      {cfg.meta && (
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                          {cfg.meta(item).filter(Boolean).map((m, i) => (
                            <span key={i} className="flex items-center gap-1">{m.icon}{m.text}</span>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KanbanBoard;
